<template>
    <div class="data-grid">
        <!-- Empty State -->
        <div v-if="!items || items.length === 0" class="data-grid-empty">
            <slot name="empty">
                <div class="flex flex-col items-center justify-center py-12">
                    <div v-if="loading" class="spinner"></div>
                    <template v-else>
                        <img v-if="emptyIcon" :src="emptyIcon" class="w-12 h-12 mb-4" />
                        <i v-else class="fa fa-inbox text-4xl text-gray-400 dark:text-gray-600 mb-4"></i>
                        <h3 class="text-base font-medium text-gray-700 dark:text-gray-300">{{ emptyMessage || 'Nenhum registro encontrado' }}</h3>
                    </template>
                </div>
            </slot>
        </div>

        <!-- Desktop Table -->
        <div v-else class="grid-desktop">
            <table class="table">
                <thead>
                    <tr>
                        <th v-for="column in columns" :key="column.key" :style="{ width: column.width }">
                            {{ column.label }}
                        </th>
                        <th v-if="actions && actions.length > 0" style="width: 15%">&nbsp;</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item, index) in items" :key="getItemKey(item, index)">
                        <td v-for="column in columns" :key="column.key">
                            <slot :name="`column-${column.key}`" :item="item" :value="getCellValue(item, column)">
                                <span v-if="column.highlight" class="destaque">{{ formatCellValue(item, column) }}</span>
                                <span v-else>{{ formatCellValue(item, column) }}</span>
                            </slot>
                        </td>
                        <td v-if="actions && actions.length > 0">
                            <slot name="actions" :item="item" :index="index">
                                <div class="widget icons inline-flex gap-2">
                                    <template v-for="action in actions">
                                        <a
                                            v-if="!action.visible || action.visible(item)"
                                            :key="action.key || action.label"
                                            @click.stop.prevent="action.handler(item, index)"
                                            :class="getActionClass(action)"
                                            :title="action.label"
                                        >
                                            <i :class="action.icon"></i>
                                        </a>
                                    </template>
                                </div>
                            </slot>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>

        <!-- Mobile Cards -->
        <div v-if="items && items.length > 0" class="grid-mobile">
            <div class="rows__carteiras">
                <div class="header__row">
                    <div v-for="column in columns" :key="column.key" class="widget">
                        <p>{{ column.label }}</p>
                    </div>
                </div>

                <div v-for="(item, index) in items" :key="getItemKey(item, index)">
                    <div class="row__client">
                        <div class="info__client">
                            <div v-for="column in columns" :key="column.key" class="widget">
                                <p class="title__mobile">{{ column.label }}</p>
                                <slot :name="`column-${column.key}`" :item="item" :value="getCellValue(item, column)">
                                    <p>{{ formatCellValue(item, column) }}</p>
                                </slot>
                            </div>

                            <div v-if="actions && actions.length > 0" class="widget icons inline-flex gap-2">
                                <slot name="actions" :item="item" :index="index">
                                    <template v-for="action in actions">
                                        <a
                                            v-if="!action.visible || action.visible(item)"
                                            :key="action.key || action.label"
                                            @click.stop.prevent="action.handler(item, index)"
                                            :class="getActionClass(action)"
                                            :title="action.label"
                                        >
                                            <i :class="action.icon"></i>
                                        </a>
                                    </template>
                                </slot>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'DataGrid',
    props: {
        items: {
            type: Array,
            default: () => []
        },
        columns: {
            type: Array,
            required: true,
            // Example: [{ key: 'name', label: 'Nome', width: '30%', formatter: (value, item) => value, highlight: false }]
        },
        actions: {
            type: Array,
            default: () => []
            // Example: [{ key: 'edit', icon: 'fa fa-edit', label: 'Editar', handler: (item, index) => {}, variant: 'edit' }]
        },
        emptyMessage: {
            type: String,
            default: 'Nenhum registro encontrado'
        },
        emptyIcon: {
            type: String,
            default: null
        },
        loading: {
            type: Boolean,
            default: false
        },
        itemKey: {
            type: String,
            default: null
        }
    },
    methods: {
        getItemKey(item, index) {
            return this.itemKey ? item[this.itemKey] : index;
        },
        getCellValue(item, column) {
            const keys = column.key.split('.');
            let value = item;
            for (const key of keys) {
                value = value?.[key];
            }
            return value;
        },
        formatCellValue(item, column) {
            const value = this.getCellValue(item, column);
            if (column.formatter && typeof column.formatter === 'function') {
                return column.formatter(value, item);
            }
            return value ?? '';
        },
        getActionClass(action) {
            const baseClass = action.variant === 'delete' ? 'ico__delete' : 'ico__edit';
            return baseClass;
        }
    }
}
</script>

<style lang="scss" scoped>
.data-grid {
    width: 100%;
}

.data-grid-empty {
    @apply bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg;
}

/* Desktop Table Styles */
.grid-desktop {
    display: block;

    @media (max-width: 768px) {
        display: none;
    }
}

/* Mobile Cards Styles */
.grid-mobile {
    @media (max-width: 768px) {
        display: block !important;
    }
}

.table {
    @apply w-full border-collapse;
    @apply bg-white dark:bg-slate-800;
    @apply border border-gray-200 dark:border-slate-700;
    @apply rounded-lg overflow-hidden;
}

.table thead {
    @apply bg-gray-50 dark:bg-slate-700;
}

.table thead th {
    @apply px-4 py-3 text-left text-xs font-semibold text-gray-700 dark:text-gray-200 uppercase tracking-wider;
}

.table tbody tr {
    @apply border-t border-gray-200 dark:border-slate-700;
    @apply hover:bg-gray-50 dark:hover:bg-slate-700/50;
    @apply transition-colors duration-150;
}

.table tbody td {
    @apply px-4 py-3 text-sm text-gray-900 dark:text-gray-100;
}

.destaque {
    @apply font-semibold text-gray-900 dark:text-gray-100;
}

/* Action Icons */
.ico__edit,
.ico__delete {
    @apply inline-flex items-center justify-center;
    @apply w-8 h-8 rounded-lg;
    @apply text-gray-600 dark:text-gray-400;
    @apply hover:bg-gray-100 dark:hover:bg-slate-700;
    @apply transition-colors duration-200;
    @apply cursor-pointer;
}

.ico__edit i,
.ico__delete i {
    @apply text-base;
}

.ico__delete {
    @apply text-red-600 dark:text-red-400;
    @apply hover:bg-red-50 dark:hover:bg-red-900/20;
}

/* Spinner */
.spinner {
    @apply w-10 h-10 border-4 border-gray-200 dark:border-slate-700;
    @apply border-t-blue-600 dark:border-t-blue-500;
    @apply rounded-full animate-spin;
}

/* Mobile Styles */
.rows__carteiras {
    @apply space-y-3;
}

.header__row {
    display: none;
}

.row__client {
    @apply bg-white dark:bg-slate-800;
    @apply border border-gray-200 dark:border-slate-700;
    @apply rounded-lg p-4;
    @apply shadow-sm;
}

.info__client {
    @apply space-y-3;
}

.info__client .widget {
    @apply flex flex-col gap-1;
}

.title__mobile {
    @apply text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase;
}

.info__client .widget p:not(.title__mobile) {
    @apply text-sm text-gray-900 dark:text-gray-100;
}

.info__client .widget.icons {
    @apply flex-row justify-end pt-2 border-t border-gray-200 dark:border-slate-700 mt-2;
}
</style>
